import { parseProgress, serializeProgress, type PlayerProgress } from '@nine/shared';

const KEY = 'nine-swords.save.v1';

/** Character slots per account (slot 0 keeps the key saves always had). */
export const SLOTS = 3;
const slotKey = (slot: number) => (slot ? `${KEY}.slot${slot}` : KEY);

/** Local saves until accounts arrive with the server. Storage can be unavailable (private mode), so every call is guarded. */
export function loadSave(slot = 0): PlayerProgress | null {
  try {
    const raw = localStorage.getItem(slotKey(slot));
    return raw ? parseProgress(raw) : null;
  } catch {
    return null;
  }
}

/** Online (net/trial.ts): the server keeps the character, so this browser's saves are left as they were. */
let localSaves = true;

export function stopLocalSaves(): void {
  localSaves = false;
}

export function writeSave(p: PlayerProgress, slot = session().slot): void {
  if (!localSaves) return;
  try {
    localStorage.setItem(slotKey(slot), serializeProgress(p));
  } catch {
    // Storage full or blocked: the session continues unsaved.
  }
}

export function clearSave(slot = session().slot): void {
  try {
    localStorage.removeItem(slotKey(slot));
  } catch {
    // ignore
  }
}

/**
 * Who is playing in this tab: the account name, the server and character slot picked, and
 * whether they have entered the world (so a reload after picking goes straight in).
 */
export interface Session {
  user: string | null;
  server: string | null;
  slot: number;
  enter: boolean;
}

const SESSION_KEY = 'nine-swords.session.v1';

export function session(): Session {
  try {
    const v = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '{}') as Partial<Session>;
    return {
      user: typeof v.user === 'string' ? v.user : null,
      server: typeof v.server === 'string' ? v.server : null,
      slot: typeof v.slot === 'number' && v.slot >= 0 && v.slot < SLOTS ? v.slot : 0,
      enter: v.enter === true,
    };
  } catch {
    return { user: null, server: null, slot: 0, enter: false };
  }
}

export function setSession(patch: Partial<Session>): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...session(), ...patch }));
  } catch {
    // ignore
  }
}

/** The account name last used in this browser, to fill the login form. */
export function lastUser(): string {
  try {
    return localStorage.getItem('nine-swords.user.v1') ?? '';
  } catch {
    return '';
  }
}

export function rememberUser(name: string): void {
  try {
    localStorage.setItem('nine-swords.user.v1', name);
  } catch {
    // ignore
  }
}

const CAMERA_KEY = 'nine-swords.camera.v1';

/** Per-browser camera preference: zoom steps and camera angle. */
export interface CameraPrefs {
  zoom: number;
  /** The quarter turn the camera rests at, 0–3. */
  rot: number;
  /** The whole angle in quarter turns, 0 to 4 (off a quarter turn only with `?camera=free` or `=8`). */
  angle?: number;
}

export function loadCameraPrefs(): CameraPrefs {
  try {
    const v = JSON.parse(localStorage.getItem(CAMERA_KEY) ?? '{}') as Partial<CameraPrefs>;
    const int = (n: unknown, lo: number, hi: number) =>
      typeof n === 'number' && Number.isInteger(n) ? Math.max(lo, Math.min(hi, n)) : 0;
    // Zoom: fractional steps since the zoom became smooth (old saves hold whole ones).
    const num = (n: unknown, lo: number, hi: number) =>
      typeof n === 'number' && Number.isFinite(n) ? Math.max(lo, Math.min(hi, n)) : 0;
    const rot = int(v.rot, 0, 3);
    // Zoom: down to −2, as the widest zoom is 1.4 steps under the default on a big screen.
    return { zoom: num(v.zoom, -2, 4), rot, angle: typeof v.angle === 'number' ? num(v.angle, 0, 4) : rot };
  } catch {
    return { zoom: 0, rot: 0 };
  }
}

export function saveCameraPrefs(p: CameraPrefs): void {
  try {
    localStorage.setItem(CAMERA_KEY, JSON.stringify(p));
  } catch {
    // ignore
  }
}
